import React, { useState } from 'react';

// Use environment variable for API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const HomePage = ({ trains, startStation, endStation, error, loading }) => {
  const [selectedTrain, setSelectedTrain] = useState('');
  const [routePlan, setRoutePlan] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState('');

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price || price === "Error checking price." || price === "Price unavailable") {
      return "Price unavailable";
    }
    
    // If price is a string number, convert to number and format
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(numPrice) ? "Price unavailable" : `${numPrice.toFixed(2)} PLN`;
  };

  // Calculate total price from segments
  const calculateTotalPrice = (segments) => {
    let total = 0;
    let hasValidPrices = false;

    segments.forEach(segment => {
      if (segment.price && segment.price !== "Error checking price." && segment.price !== "Price unavailable") {
        const numPrice = typeof segment.price === 'string' ? parseFloat(segment.price) : segment.price;
        if (!isNaN(numPrice)) {
          total += numPrice;
          hasValidPrices = true;
        }
      }
    });

    return hasValidPrices ? total : null;
  };

  // Step 2: Select train and get route plan
  const handleTrainSelect = async (trainIndex) => {
    // If clicking the same train that's already selected, toggle it closed
    if (selectedTrain === trainIndex && routePlan) {
      setRoutePlan(null);
      setSelectedTrain('');
      return;
    }

    // Close any previously opened train and open the new one
    setRoutePlan(null);
    setSelectedTrain(trainIndex);
    setRouteLoading(true);
    setRouteError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/route-plan?start_station=${encodeURIComponent(startStation)}&end_station=${encodeURIComponent(endStation)}&chosen_train_index=${trainIndex}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to get route plan');
      }
      
      const data = await response.json();
      setRoutePlan(data);
    } catch (err) {
      setRouteError('Error getting route plan: ' + err.message);
      setSelectedTrain(''); // Clear selection on error
    } finally {
      setRouteLoading(false);
    }
  };

  // Render train list
  const renderTrains = () => {
    if (!trains) return null;

    return (
      <div className="trains-list">
        <h2>Available Trains:</h2>
        {Object.entries(trains).map(([index, train]) => (
          <div key={index} className={`train-item ${selectedTrain === index ? 'expanded' : ''}`}>
            <div className="train-basic-info">
              <div className="train-info">
                <strong>Train {train.train_nr}</strong>
                <p>Departure: {new Date(train.departure_time).toLocaleString()}</p>
                <p>Arrival: {new Date(train.arrival_time).toLocaleString()}</p>
              </div>
              <button 
                onClick={() => handleTrainSelect(index)}
                disabled={routeLoading && selectedTrain === index}
                className="select-train-button"
              >
                {routeLoading && selectedTrain === index ? 'Loading...' : 
                 selectedTrain === index && routePlan ? 'Close Details' : 'Select Train'}
              </button>
            </div>
            
            {/* Expandable route plan section */}
            <div className={`route-plan-expandable ${selectedTrain === index && (routePlan || (routeLoading && selectedTrain === index)) ? 'expanded' : ''}`}>
              {selectedTrain === index && routeLoading && (
                <div className="route-plan-loading">
                  <div className="loading-content">
                    <p>Loading route details...</p>
                  </div>
                </div>
              )}
              
              {selectedTrain === index && routePlan && !routeLoading && (
                <div className="route-plan-content">
                  <div className="train-details">
                    <h3>Route Details for Train {routePlan.train_nr}</h3>
                    <p><strong>From:</strong> {routePlan.departure}</p>
                    <p><strong>To:</strong> {routePlan.arrival}</p>
                    
                    {/* Price Summary */}
                    <div className="price-summary">
                      <h4>Price Information:</h4>
                      {routePlan.full_price && (
                        <p className="full-price">
                          <strong>Full Route Price: {formatPrice(routePlan.full_price)}</strong>
                        </p>
                      )}
                      {calculateTotalPrice(routePlan.station_plan) && (
                        <p className="segment-total">
                          <strong>Total Segment Prices: {formatPrice(calculateTotalPrice(routePlan.station_plan))}</strong>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="seating-plan">
                    <h4>Seating Information:</h4>
                    {routePlan.station_plan.map((segment, segmentIndex) => (
                      <div 
                        key={segmentIndex} 
                        className={`segment ${segment.status}`}
                      >
                        <div className="segment-header">
                          <p className="segment-route">
                            {segment.from && segment.to ? 
                              `${Array.isArray(segment.from) ? segment.from[0] : segment.from} → ${Array.isArray(segment.to) ? segment.to[0] : segment.to}` 
                              : 'Full Route'
                            }
                          </p>
                          {segment.price && (
                            <p className="segment-price">
                              <strong>{formatPrice(segment.price)}</strong>
                            </p>
                          )}
                        </div>
                        <p className={`segment-message ${segment.status}`}>
                          {segment.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="search-container">
      {/* Welcome Instructions - Show only when no trains are loaded */}
      {!trains && !loading && !error && (
        <div className="welcome-section">
          <div className="welcome-content">
            <h2>Welcome to WhereToSeat! 🚂</h2>
            <p>Find the best seats on Polish trains with real-time availability information.</p>
            
            <div className="instructions">
              <h3>How to use:</h3>
              <ul>
                <li>📍 Enter your <strong>start</strong> and <strong>destination</strong> stations</li>
                <li>📅 Optionally select a <strong>date and time</strong> (defaults to now)</li>
                <li>🔍 Click <strong>Search</strong> to find available trains</li>
                <li>🚆 Select any train to see <strong>detailed seat availability</strong> and pricing</li>
                <li>💺 View segment-by-segment seating information for your journey</li>
              </ul>
            </div>
            
            <div className="tips">
              <h4>💡 Tips:</h4>
              <p>• Green segments = seats available, Red = standing room only</p>
              <p>• Each train shows different pricing options</p>
              <p>• This is a VERY earliy demo application and may contain errors</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || routeError) && (
        <div className="error-message">
          {error || routeError}
        </div>
      )}

      {/* Loading Indicator for search results */}
      {loading && (
        <div className="loading">
          Searching for trains...
        </div>
      )}

      {/* Train List */}
      {renderTrains()}
    </section>
  );
};

export default HomePage;
